import React from "react";
import { Button, ButtonGroup, FormControl, FormGroup, FormLabel } from "react-bootstrap";
import { DisplayBox } from "../../components";
import { ApiHelper, ArrayHelper, PendingCommand, PlaylistMessageConditionInterface, PlaylistMessageInterface, QuestionInterface } from "../../helpers";


interface Props {
  playlistMessage: PlaylistMessageInterface
  sendCommand: (cmd: PendingCommand) => void
}


export const Conditions: React.FC<Props> = props => {

  const [playlistMessage, setPlaylistMessage] = React.useState<PlaylistMessageInterface>(null);
  const [questions, setQuestions] = React.useState<QuestionInterface[]>([]);

  const [questionId, setQuestionId] = React.useState(0);
  const [operator, setOperator] = React.useState("=");
  const [value, setValue] = React.useState("");
  const [dataType, setDataType] = React.useState("bool");
  const [error, setError] = React.useState("");


  const handleChange = (e: any) => {
    let val = e.currentTarget.value;
    switch (e.currentTarget.id) {
      case "question": setQuestionId(parseInt(val, 0)); break;
      case "operator": setOperator(val); break;
      case "value": setValue(val); break;
      case "dataType": setDataType(val); break;
    }
    setError("");
  }

  // Helper functions for better UX
  const getOperatorText = (op: string) => {
    const operators: { [key: string]: string } = {
      "=": "equals",
      "<>": "does not equal",
      "<": "is less than",
      "<=": "is less than or equal to",
      ">": "is greater than",
      ">=": "is greater than or equal to",
      "contains": "contains",
      "startsWith": "starts with",
      "endsWith": "ends with"
    };
    return operators[op] || op;
  }

  const getDataTypeText = (type: string) => {
    const types: { [key: string]: string } = {
      "bool": "True/False",
      "double": "Number",
      "string": "Text"
    };
    return types[type] || type;
  }

  const validateCondition = () => {
    if (!questionId || questionId === 0) {
      setError("Please select a question");
      return false;
    }
    if (!value.trim()) {
      setError("Please enter a value");
      return false;
    }
    if (dataType === "double" && isNaN(Number(value))) {
      setError("Please enter a valid number");
      return false;
    }
    return true;
  }



  const handleSave = () => {
    props.sendCommand({ action: "updateConditions", parameter: playlistMessage })
  }


  const init = () => {
    if (props.playlistMessage) {
      setPlaylistMessage(props.playlistMessage);
      let params = new URLSearchParams(window.location.search);
      ApiHelper.get<QuestionInterface[]>("/questions/feed/" + params.get("feedId"))
        .then(data => {
          setQuestions(data || [])
          if ((data || []).length > 0) setQuestionId(data[0].id);
        })
        .catch(err => console.error("Failed to load feed questions for conditions:", err));
    }
  }


  const loadConditions = async () => {
    if (playlistMessage && !playlistMessage.deleteConditionIds) {
      let conditions: PlaylistMessageConditionInterface[] = [];
      if (playlistMessage.id > 0) {
        conditions = await ApiHelper.get<PlaylistMessageConditionInterface[]>("/playlistMessageConditions/playlistMessage/" + playlistMessage.id) || [];
      }
      let pm = { ...playlistMessage };
      pm.conditions = conditions;
      pm.deleteConditionIds = [];
      setPlaylistMessage(pm);
    }
  }

  const handleDelete = (index: number) => {
    let pm = { ...playlistMessage };
    const conditions: PlaylistMessageConditionInterface[] = pm.conditions.splice(index, 1);
    if (conditions[0].id) pm.deleteConditionIds.push(conditions[0].id);
    setPlaylistMessage(pm);
  }

  React.useEffect(init, [props.playlistMessage]);  //eslint-disable-line
  React.useEffect(() => { loadConditions() }, [playlistMessage]);  //eslint-disable-line

  const getDoneButton = () => {
    return <Button size="sm" variant="success" onClick={(e) => { e.preventDefault(); handleSave(); }}>Done</Button>
  }

  const getQuestions = () => {
    const result: React.ReactElement[] = [];
    questions?.forEach(q => {
      result.push(<option value={q.id} key={q.id}>{q.title}</option>);
    })
    return result;
  }

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!validateCondition()) return;

    const pm = { ...playlistMessage };
    pm.conditions.push({ questionId, operator, value, dataType });
    setPlaylistMessage(pm);

    // Reset form
    setValue("");
    setError("");
  }


  const getConditionRows = () => {
    const result: React.ReactElement[] = [];
    if (playlistMessage?.conditions) {
      let idx = 0;
      for (let c of playlistMessage.conditions) {
        const q: QuestionInterface = ArrayHelper.getOne(questions, "id", c.questionId);
        if (q) {
          const index = idx;
          result.push(
            <div key={index} className="conditions-rule-row">
              <div className="conditions-rule-question">{q.title}</div>
              <div className="conditions-rule-summary">
                <span className="conditions-op-chip">{getOperatorText(c.operator)}</span>
                <span className="conditions-val-chip">{c.value}</span>
                <span className="conditions-type-chip">{getDataTypeText(c.dataType)}</span>
              </div>
              <button
                className="conditions-delete-btn"
                aria-label="Delete condition"
                onClick={(e) => { e.preventDefault(); handleDelete(index); }}
              >
                <i className="far fa-trash-alt" aria-hidden="true"></i>
              </button>
            </div>
          );
        }
        idx++;
      }
    }
    return result;
  }

  const dataTypes = [
    { value: "bool", label: "True/False" },
    { value: "double", label: "Number" },
    { value: "string", label: "Text" },
  ];

  return (
    <>
      <DisplayBox headerIcon="fas fa-equals" headerText="Conditions" editContent={getDoneButton()}>
        <div className="conditions-editor">

          {/* Add-condition panel */}
          <div className="conditions-add-panel">
            <div className="conditions-add-panel-title">Add Condition</div>

            <FormGroup className="conditions-field-row">
              <FormLabel className="conditions-field-label">Question</FormLabel>
              <FormControl as="select" id="question" value={questionId} onChange={handleChange}>
                <option value={0}>Select question...</option>
                {getQuestions()}
              </FormControl>
            </FormGroup>

            <FormGroup className="conditions-field-row">
              <FormLabel className="conditions-field-label">Operator</FormLabel>
              <FormControl as="select" id="operator" value={operator} onChange={handleChange}>
                <option value="=">equals</option>
                <option value="<>">does not equal</option>
                <option value="<">is less than</option>
                <option value="<=">is less than or equal to</option>
                <option value=">=">is greater than or equal to</option>
                <option value=">">is greater than</option>
              </FormControl>
            </FormGroup>

            <FormGroup className="conditions-field-row">
              <FormLabel className="conditions-field-label">Value</FormLabel>
              <FormControl id="value" value={value} onChange={handleChange} placeholder="Enter value..." />
            </FormGroup>

            <FormGroup className="conditions-field-row">
              <FormLabel className="conditions-field-label">Data Type</FormLabel>
              <ButtonGroup className="conditions-datatype-segmented">
                {dataTypes.map(dt => (
                  <Button
                    key={dt.value}
                    variant={dataType === dt.value ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={(e) => { e.preventDefault(); setDataType(dt.value); setError(""); }}
                    aria-pressed={dataType === dt.value}
                  >
                    {dt.label}
                  </Button>
                ))}
              </ButtonGroup>
            </FormGroup>

            {error && (
              <div className="conditions-error" role="alert">
                <i className="fas fa-exclamation-circle me-2" aria-hidden="true"></i>
                {error}
              </div>
            )}

            <Button variant="primary" className="conditions-add-btn w-100" onClick={handleAdd}>
              <i className="fas fa-plus me-2" aria-hidden="true"></i>
              Add Condition
            </Button>
          </div>

          {/* Rules list */}
          {playlistMessage?.conditions?.length > 0 && (
            <div className="conditions-rules-list">
              <div className="conditions-rules-header">Active Rules</div>
              {getConditionRows()}
            </div>
          )}

        </div>
      </DisplayBox>
    </>
  );
};
